import React, { useState, useCallback } from 'react';

/**
 * StudentTableFilters
 *
 * Filter bar component for the student roster table.
 * Includes bulk actions, subject filter, sort, date picker, and search.
 */

// Bulk action options
const BULK_ACTIONS = [
  { value: '', label: 'Bulk Action' },
  { value: 'export', label: 'Export Selected' }
];

// Sort options
const SORT_OPTIONS = [
  { value: 'desc', label: 'DESC' },
  { value: 'asc', label: 'ASC' }
];

const StudentTableFilters = ({
  filters,
  onFiltersChange,
  onReset,
  sortConfig,
  onSortChange,
  availableSubjects,
  selectedCount,
  onBulkAction
}) => {
  const [bulkAction, setBulkAction] = useState('');

  // Handle subject change
  const handleSubjectChange = useCallback((e) => {
    onFiltersChange({ subject: e.target.value });
  }, [onFiltersChange]);

  // Handle search change with debounce
  const handleSearchChange = useCallback((e) => {
    onFiltersChange({ searchTerm: e.target.value });
  }, [onFiltersChange]);

  // Handle date change
  const handleDateChange = useCallback((e) => {
    const value = e.target.value;
    onFiltersChange({ dateFrom: value || null });
  }, [onFiltersChange]);

  // Handle sort direction change
  const handleSortDirectionChange = useCallback((e) => {
    onSortChange({ ...sortConfig, direction: e.target.value });
  }, [sortConfig, onSortChange]);

  // Handle bulk action selection
  const handleBulkActionChange = (e) => {
    setBulkAction(e.target.value);
  };

  // Apply bulk action
  const handleApplyBulkAction = () => {
    if (bulkAction && onBulkAction) {
      onBulkAction(bulkAction);
      setBulkAction('');
    }
  };

  // Handle reset
  const handleReset = () => {
    setBulkAction('');
    onReset();
  };

  return (
    <div className="roster-filters">
      {/* Bulk Actions Group */}
      <div className="roster-filters__group roster-filters__group--bulk">
        <select
          className="roster-filters__select roster-filters__bulk-select"
          value={bulkAction}
          onChange={handleBulkActionChange}
          disabled={selectedCount === 0}
        >
          {BULK_ACTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          className="roster-filters__btn roster-filters__btn--apply"
          onClick={handleApplyBulkAction}
          disabled={!bulkAction || selectedCount === 0}
        >
          Apply
        </button>
        <button
          className="roster-filters__btn roster-filters__btn--reset"
          onClick={handleReset}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M2.5 7C2.5 4.51472 4.51472 2.5 7 2.5C8.58129 2.5 9.97121 3.3292 10.75 4.5625M11.5 7C11.5 9.48528 9.48528 11.5 7 11.5C5.41871 11.5 4.02879 10.6708 3.25 9.4375M10.5 2V5H7.5M3.5 9H6.5V12"
              stroke="currentColor"
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Reset
        </button>
      </div>

      {/* Filter Controls Group */}
      <div className="roster-filters__group roster-filters__group--controls">
        {/* Subject Filter */}
        <div className="roster-filters__control">
          <label className="roster-filters__label">Subject</label>
          <select
            className="roster-filters__select"
            value={filters.subject}
            onChange={handleSubjectChange}
          >
            <option value="">All Subjects</option>
            {availableSubjects.map(subject => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
        </div>

        {/* Sort Direction */}
        <div className="roster-filters__control">
          <label className="roster-filters__label">Sort By</label>
          <select
            className="roster-filters__select"
            value={sortConfig.direction}
            onChange={handleSortDirectionChange}
          >
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date Filter */}
        <div className="roster-filters__control">
          <label className="roster-filters__label">Date</label>
          <input
            type="date"
            className="roster-filters__input roster-filters__date"
            value={filters.dateFrom || ''}
            onChange={handleDateChange}
            placeholder="MMMM d, yyyy"
          />
        </div>

        {/* Search */}
        <div className="roster-filters__control roster-filters__control--search">
          <label className="roster-filters__label">Search</label>
          <div className="roster-filters__search-wrapper">
            <svg className="roster-filters__search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14L10.5 10.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              className="roster-filters__input"
              placeholder="Search..."
              value={filters.searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentTableFilters;
